import { computed, effect, inject, Injectable, InjectionToken, isDevMode, Provider, Signal, signal, Type } from '@angular/core';

import { signalMethod } from '@ngrx/signals';

import { AsyncRegistryInitializer, provideServicesRegistry, ServicesRegistry } from '@kros-sk/core/registry';

import { SCHEMA_STATE } from '../address/schema-state';
import { DataState } from '../model/data-state';
import { SCHEMA_CONTEXT } from '../model/schema-context';
import { SchemaQueryHost } from '../query/schema-query';

export interface SchemaDataSource<TData = unknown> {
  readonly state: Signal<DataState<TData>>;
}

export type SchemaDataSourceEntry = AsyncRegistryInitializer<Type<SchemaDataSource>>;

export const SCHEMA_DATA_SOURCES_REGISTRY = new InjectionToken<ServicesRegistry<SchemaDataSource>>('SCHEMA_DATA_SOURCES_REGISTRY');

export function provideSchemaDataSources(...dataSources: SchemaDataSourceEntry[]): Provider {
  return provideServicesRegistry(SCHEMA_DATA_SOURCES_REGISTRY, ...dataSources);
}

export const SCHEMA_DATA_SOURCE_CONFIG = new InjectionToken<unknown>('SCHEMA_DATA_SOURCE_CONFIG');

export function injectSchemaDataSourceConfig<T = unknown>(): T {
  return inject<T>(SCHEMA_DATA_SOURCE_CONFIG);
}

@Injectable()
export class SchemaDataSourceHost {
  readonly #parent = inject(SchemaDataSourceHost, { skipSelf: true, optional: true });

  readonly #dataSource = signal<SchemaDataSource | null>(null);

  readonly #declaredType = signal<Signal<string | null | undefined> | null>(null);

  readonly #declaresProducer = computed(() => !!this.#declaredType()?.());

  /**
   * Nearest producer wins. Every schema node hosts one of these, so most of them hold nothing and pass the
   * source above straight through; a host that answered `null` for such a level would cut its subtree off
   * from the source above it.
   *
   * A level that declares a producer answers with its own source instead — `null` while that source is
   * still connecting. Falling through there would hand the subtree an unrelated source, the one above, for
   * as long as the connection takes.
   *
   * Annotated because the chain to the parent host makes the type self-referential, which TypeScript
   * cannot infer.
   */
  readonly dataSource: Signal<SchemaDataSource | null> = computed(
    () => this.#dataSource() ?? (this.#declaresProducer() ? null : (this.#parent?.dataSource() ?? null))
  );

  /** Whether a producer is declared here or above — what tells "not connected yet" apart from "none at all". */
  readonly hasProducer: Signal<boolean> = computed(
    () => this.#dataSource() !== null || this.#declaresProducer() || (this.#parent?.hasProducer() ?? false)
  );

  readonly register = signalMethod<SchemaDataSource | null>((instance) => this.#dataSource.set(instance));

  /**
   * The producer this level declares, read as it changes. Set synchronously by whoever connects it, before
   * anything below reads the host — an effect would leave the first reads falling through to the source above.
   */
  declareProducer(type: Signal<string | null | undefined>): void {
    this.#declaredType.set(type);
  }
}

export function injectSchemaDataSource<T extends SchemaDataSource = SchemaDataSource>(): Signal<T | null> {
  return inject(SchemaDataSourceHost).dataSource as Signal<T | null>;
}

export function connectSchemaDataSource(
  dataSourceType: Signal<string | null | undefined>,
  config?: Signal<unknown>,
  handed?: Signal<DataState | undefined>
): void {
  const registry = inject(SCHEMA_DATA_SOURCES_REGISTRY, { optional: true });

  if (!registry) {
    /**
     * Every schema node connects, but only some injectors register data sources at all — the showcase
     * side panel and create-company render schemas with none in scope. A node that wants nothing is
     * the normal case there; a node that names a source and finds no registry is a wiring bug, and
     * saying so beats rendering an empty component forever.
     */
    if (isDevMode()) {
      effect(() => {
        const type = dataSourceType();

        if (type) {
          console.warn(
            `Schema node declares data source "${type}", but no SCHEMA_DATA_SOURCES_REGISTRY is reachable from this injector, ` +
              `so nothing will load. Add provideSchemaDataSources(…) where this schema is rendered.`
          );
        }
      });
    }

    return;
  }

  /**
   * A data source cannot reach `SchemaQueryHost` by injecting it. `ServicesRegistry` builds the
   * instance under `ownerInjector.get(EnvironmentInjector)`, which is the environment chain and holds
   * none of the outlet's element providers — the same reason `SCHEMA_DATA_SOURCE_CONFIG` is handed
   * over here rather than injected. Passing the host through is what lets a source build its request
   * from the sort and filter the schema declared, instead of reading the URL itself.
   *
   * Optional because `connectSchemaDataSource` is also used by hosts that are not schema nodes and
   * provide only `SchemaDataSourceHost`.
   */
  const queryHost = inject(SchemaQueryHost, { optional: true });

  /**
   * Handed over for the same reason. A host may publish its context on a component, and a source that read it
   * from the environment chain would substitute `$$context.X$$` from another context than the `$context.X` its
   * node reads — or from none.
   */
  const context = inject(SCHEMA_CONTEXT, { optional: true });

  /**
   * What `$data`, `$state` and `$source` read in the source's own config (D5): the state its node was handed — its own
   * state is what it produces — and the source above its node. Both live in element injectors the source cannot reach,
   * so they travel with the context.
   */
  const above = inject(SchemaDataSourceHost, { skipSelf: true, optional: true });
  const host = inject(SchemaDataSourceHost);

  host.declareProducer(dataSourceType);

  const resolved = registry.connect(dataSourceType, {
    providers: [
      { provide: SCHEMA_DATA_SOURCE_CONFIG, useFactory: () => config?.() ?? null },
      ...(queryHost ? [{ provide: SchemaQueryHost, useValue: queryHost }] : []),
      ...(context ? [{ provide: SCHEMA_CONTEXT, useValue: context }] : []),
      ...(handed ? [{ provide: SCHEMA_STATE, useValue: handed }] : []),
      ...(above ? [{ provide: SchemaDataSourceHost, useValue: above }] : []),
    ],
  });
  host.register(resolved.instance);
}
